/**
 * CheckpointPage — Interactive checkpoint replay for completed sessions.
 *
 * Left: Live browser stream (MJPEG iframe, interactive)
 * Right: Event timeline — click any action to restore browser to that checkpoint
 *
 * Route: /checkpoint/:session_id
 */

import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSessions';
import { ParsedEvent } from '../types/session';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export const CheckpointPage: React.FC = () => {
  const { session_id } = useParams<{ session_id: string }>();
  const navigate = useNavigate();
  const { data: session, isLoading, error } = useSession(session_id);

  const [activeCheckpoint, setActiveCheckpoint] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [streamKey, setStreamKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const streamingUrl = session?.streaming_url || null;
  const browserSessionId = session?.web_agent_session_id || session_id;
  const events = session?.session_log || [];

  const handleRestore = useCallback(async (event: ParsedEvent) => {
    if (!session_id || !event.function_call_id || restoringId) return;

    const checkpointId = `${browserSessionId}-${event.function_call_id}`;
    setRestoringId(event.function_call_id);

    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post(
        `${API_BASE_URL}/api/sessions/${session_id}/restore`,
        { checkpointId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (resp.data?.success) {
        setActiveCheckpoint(event.function_call_id);
        setStreamKey(k => k + 1);
        toast.success(`Restored to: ${event.purpose || event.function_call_id}`);
      } else {
        toast.error(resp.data?.error || 'Restore failed');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const msg = axiosErr?.response?.data?.error || axiosErr.message || 'Restore failed';
      toast.error(msg);
    } finally {
      setRestoringId(null);
    }
  }, [session_id, browserSessionId, restoringId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-md p-6 max-w-md">
          <p className="text-red-800 text-sm">{error?.message || 'Session not found'}</p>
          <button onClick={() => navigate(-1)} className="mt-3 text-sm text-red-600 hover:text-red-500">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600" aria-label="Back">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">Checkpoint Session</h1>
            <p className="text-xs text-gray-500">{session_id}</p>
          </div>
          <span className="text-xs text-gray-400">Click any action to restore browser to that point</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex gap-3 p-3 min-h-0">
        {/* Left: Browser Stream */}
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
          <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
            <h2 className="text-sm font-semibold text-gray-900">
              Browser
              {activeCheckpoint && (
                <span className="ml-2 text-xs font-normal text-blue-600">
                  @ {events.find(e => e.function_call_id === activeCheckpoint)?.purpose || activeCheckpoint}
                </span>
              )}
            </h2>
            {streamingUrl && (
              <a href={streamingUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700">
                Open in new tab
              </a>
            )}
          </div>
          <div className="flex-1 relative">
            {streamingUrl ? (
              <iframe
                key={streamKey}
                ref={iframeRef}
                src={streamingUrl}
                className="absolute inset-0 w-full h-full border-none"
                title="Browser Checkpoint"
                allow="fullscreen"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                No browser stream available for this session
              </div>
            )}
          </div>
        </div>

        {/* Right: Events Timeline */}
        <div className="w-[420px] bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col flex-shrink-0">
          <div className="bg-gray-100 border-b border-gray-200 px-3 py-2 flex-shrink-0">
            <h2 className="text-xs font-semibold text-gray-900">
              Snapshots ({events.filter(e => e.type === 'action' && e.function_call_id).length})
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {events.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-xs">No events</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {(() => {
                  // Build snapshot list: each snapshot = state after action N completes
                  // (= state right before action N+1).
                  // We pair each 'action' event with its following 'action_success'/'action_failed'.
                  const actionEvents = events.filter(e => e.type === 'action' && e.function_call_id);
                  const successMap = new Map<string, ParsedEvent>();
                  for (const e of events) {
                    if ((e.type === 'action_success' || e.type === 'action_failed') && e.function_call_id) {
                      successMap.set(e.function_call_id, e);
                    }
                  }
                  const finalEvent = events.find(e => e.type === 'final_response');

                  return (
                    <>
                      {actionEvents.map((event, stepIdx) => {
                        const result = successMap.get(event.function_call_id!);
                        const failed = result?.type === 'action_failed';
                        const isActive = activeCheckpoint === event.function_call_id;
                        const isRestoring = restoringId === event.function_call_id;

                        return (
                          <div
                            key={event.function_call_id}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                              isActive ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                            } ${isRestoring ? 'opacity-60' : ''} transition-colors`}
                            onClick={() => handleRestore(event)}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                                failed ? 'bg-red-200 text-red-700' :
                                isActive ? 'bg-blue-500 text-white' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {stepIdx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {event.purpose || `Step ${stepIdx + 1}`}
                                </p>
                              </div>
                              {isRestoring && (
                                <svg className="animate-spin h-4 w-4 text-blue-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              )}
                              {!isRestoring && (
                                <svg className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-500' : 'text-gray-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {finalEvent && (
                        <div className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 bg-green-200 text-green-700">F</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {finalEvent.final_response
                                  ? typeof finalEvent.final_response === 'string'
                                    ? finalEvent.final_response.length > 80
                                      ? finalEvent.final_response.slice(0, 80) + '...'
                                      : finalEvent.final_response
                                    : JSON.stringify(finalEvent.final_response).slice(0, 80) + '...'
                                  : 'Final response'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
